import { ResolverTag } from '@ps/web-core-enums';
import { Translation } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import React from 'react';
import { Field, useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import { select } from '@swf/core/store';

import CPRImage from '../../../../../../../../assets/cpr-number.svg';
import { TaxCodeType } from '../../../../../../../../validators/taxCode';
import InfoPrompt from '../../../../../../../InfoPrompt/InfoPrompt';
import TextInput from '../../../../../../../TextInput/TextInput';
import { isDenmarkCitizen } from '../../../../../../utils/rmf';
import DocumentNumberField from '../../../../../RMFDocumentation/components/DocumentNumberField/DocumentNumberField';
import DocumentTypeField from '../../../../../RMFDocumentation/components/DocumentTypeField/DocumentTypeField';
import IssueDateField from '../../../../../RMFDocumentation/components/IssueDateField/IssueDateField';
import RMFDropDown from '../../../../../RMFDropDown/RMFDropDown';
import TaxCodeField from '../../components/TaxCodeField/TaxCodeField';

import {
  TXTCLI_Female,
  TXTCLI_Gender,
  TXTCLI_If_you_require_further_assista_ELL,
  TXTCLI_Male,
  TXTCLI_Please_select_genderX,
  TXTCLI_Select_Gender,
  TXTWCL_CPR_number,
  TXTWCL_Document_number,
  TXTWCL_Enter_the_authority_who_issued_ELL,
  TXTWCL_Issuing_authority,
  TXTWCL_Please_enter_your_CPR_number_a_ELL,
  TXTWCL_Please_enter_your_identificati_ELL_1
} from './DKFields.messages.json';
import { fieldWrapper, promptImage } from './DKFields.scss';
import IntentToPlay from './components/IntentToPlay';

const DOC_NUMBER_MAX_LENGTH = 20;
const FIELD_TYPE = 'gender';
const DOC_ISSUING_AUTHORITY = 'docIssuePlace';

const parseIssuingAuthority = (value: string) => value.replace(/[/~!@#$&%+^*_|\\=?:"<>{}[\]]/g, '');

enum GenderValues {
  MALE = 'M',
  FEMALE = 'F'
}

const OPTIONS: { text: JSX.Element; value: string }[] = [
  {
    text: <Translation {...TXTCLI_Male} />,
    value: GenderValues.MALE
  },
  {
    text: <Translation {...TXTCLI_Female} />,
    value: GenderValues.FEMALE
  }
];

const DKFields: React.FC = () => {
  const signupCountry = useSelector(select.account.getCountry);

  const {
    input: { value: country }
  } = useField('country', { initialValue: signupCountry });

  const {
    input: { value: citizenship }
  } = useField('citizenship', { subscription: { value: true }, initialValue: signupCountry });

  return (
    <>
      {isDenmarkCitizen(citizenship) ? (
        <>
          <TaxCodeField
            taxCodeType={TaxCodeType.CPR}
            className={fieldWrapper}
            labelContent={
              <InfoPrompt title={{ translation: TXTWCL_CPR_number }} variant="verification">
                <Translation tagName="p" {...TXTWCL_Please_enter_your_CPR_number_a_ELL} />
                <Translation
                  tagName="p"
                  values={{
                    a: (msg: React.ReactNode) => (
                      <ResolverLink resolverTag={ResolverTag.HelpCenter}>{msg}</ResolverLink>
                    )
                  }}
                  {...TXTCLI_If_you_require_further_assista_ELL}
                />
                <CPRImage className={promptImage} />
              </InfoPrompt>
            }
          />
          <IntentToPlay />
        </>
      ) : (
        <>
          <div>
            <Field name="nationalDocumentNumber">
              {() => (
                <input type="hidden" name="nationalDocumentNumber" id="nationalDocumentNumber" />
              )}
            </Field>
          </div>
          <DocumentTypeField country={country} citizenship={citizenship} />
          <DocumentNumberField
            label={TXTWCL_Document_number}
            maxLength={DOC_NUMBER_MAX_LENGTH}
            validator={[required(TXTWCL_Please_enter_your_identificati_ELL_1)]}
          />
          <IssueDateField />
          <TextInput
            id={DOC_ISSUING_AUTHORITY}
            parse={parseIssuingAuthority}
            label={TXTWCL_Issuing_authority}
            checkDisabled
            validators={[required(TXTWCL_Enter_the_authority_who_issued_ELL)]}
          />
          <RMFDropDown
            id={FIELD_TYPE}
            label={TXTCLI_Gender}
            options={OPTIONS}
            validators={[required(TXTCLI_Please_select_genderX)]}
            className={fieldWrapper}
            placeholder={<Translation {...TXTCLI_Select_Gender} />}
          />
          <IntentToPlay />
        </>
      )}
    </>
  );
};

export default DKFields;
